import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import type { ColDef } from "ag-grid-enterprise";
import { Suspense, lazy, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import {
  DevCheatOrderType,
  DevCheatRouteType,
  useDevCheatMutation,
  useFeatureflags,
  useSessionUser,
} from "@web-app/api";
import {
  Button,
  ColumnDropdown,
  Icon,
  Input,
  OrdersFilterDropdown,
} from "@web-app/components";
import useDispatchStore, { AppFilterType } from "@web-app/store/dispatchStore";

import BulkActionsTooltip from "../BulkActions";
import CreateOrderDrawer from "../CreateOrderDrawer";
import MarketsDropdown from "../MarketsDropdown";

const BulkImportModal = lazy(
  () => import("../../dispatchPage/BulkImportModal"),
);

const BulkOptimizeRoutesModal = lazy(
  () => import("../../dispatchPage/BulkOptimizeRoutesModal"),
);

export default function TopNavigation({
  appFilter,
  columnDefs,
  debouncedSearchOrders,
  isDateRange,
  onExport,
  setAppFilter,
  setColumnDefs,
}: {
  appFilter: AppFilterType;
  columnDefs?: ColDef[];
  debouncedSearchOrders?: (searchText: string) => void;
  isDateRange?: boolean;
  onExport?: () => void;
  setAppFilter: (appFilter: AppFilterType) => void;
  setColumnDefs?: (columnDefs: ColDef[]) => void;
}) {
  const { data: user } = useSessionUser();
  const { hasFeature } = useFeatureflags();
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isBulkOptimizeRoutesModalOpen, setIsBulkOptimizeRoutesModalOpen] =
    useState(false);

  const {
    selectedSidebarOrderIds,
    selectedSidebarRouteIds,
    routeModalMode,
    pendingBroJobId,
  } = useDispatchStore();

  useEffect(() => {
    if (pendingBroJobId) {
      setIsBulkOptimizeRoutesModalOpen(true);
    }
  }, [pendingBroJobId]);

  const isDevCheatEnabled = hasFeature("devCheatCode");

  const { createOrder: devCreateOrder, createRoute: devCreateRoute } =
    useDevCheatMutation();

  const getLocalDateString = (isoString: string | undefined): string => {
    // Get the date as it appears in the user's local timezone
    const dateSource = isoString ? new Date(isoString) : new Date();
    const year = dateSource.getFullYear();
    const month = String(dateSource.getMonth() + 1).padStart(2, "0");
    const day = String(dateSource.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleQuickCreateOrder = async (type: DevCheatOrderType) => {
    if (!user) return;

    // Get the date as displayed in user's local timezone (YYYY-MM-DD format)
    const date = getLocalDateString(appFilter.scheduledDateTimeStart);

    toast.promise(
      devCreateOrder.mutateAsync({
        payload: {
          date,
        },
        type,
      }),
      {
        error: "Failed to create order",
        loading: "Creating order...",
        success: "Order created successfully!",
      },
    );
  };

  const handleQuickCreateRoute = async (type: DevCheatRouteType) => {
    if (!user) return;

    // Get the date as displayed in user's local timezone (YYYY-MM-DD format)
    const date = getLocalDateString(appFilter.scheduledDateTimeStart);

    toast.promise(
      devCreateRoute.mutateAsync({
        payload: {
          date,
        },
        type,
      }),
      {
        error: "Failed to create route",
        loading: "Creating route with orders...",
        success: "Route created successfully!",
      },
    );
  };

  return (
    <div className="flex justify-between">
      <div className="flex items-center gap-3">
        {routeModalMode !== "edit" && (
          <Menu as="div">
            {
              <MenuButton>
                <Button icon="plus" size="sm" iconClassName="hidden 2xl:flex">
                  Create Order
                </Button>
              </MenuButton>
            }

            <MenuItems
              transition
              anchor="bottom start"
              className="z-50 w-[180px] origin-top-right mt-1 shadow-md rounded-xl border border-white bg-white p-1 font-normal text-black  transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            >
              <MenuItem as="div" className="hover:bg-gray-100">
                <button
                  onClick={() => setIsOrderModalOpen(true)}
                  className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                >
                  <Icon name="locationPin" className="h-4 w-4" />
                  <span className="font-normal text-xs">Single Order</span>
                </button>
              </MenuItem>
              <MenuItem as="div" className="hover:bg-gray-100">
                <button
                  onClick={() => setIsBulkImportModalOpen(true)}
                  className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                >
                  <Icon name="fileExport" className="h-4 w-4" />
                  <span className="font-normal text-xs">Bulk Import</span>
                </button>
              </MenuItem>
              {isDevCheatEnabled && (
                <>
                  <div className="border-t border-gray-200 my-1" />
                  <div className="px-3 py-1">
                    <span className="text-xs text-gray-500 font-medium">
                      Quick Create (Dev)
                    </span>
                  </div>
                  <MenuItem as="div" className="hover:bg-gray-100">
                    <button
                      onClick={() => handleQuickCreateOrder("single-pickup")}
                      className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                    >
                      <Icon name="locationPin" className="h-4 w-4" />
                      <span className="font-normal text-xs">Single Pickup</span>
                    </button>
                  </MenuItem>
                  <MenuItem as="div" className="hover:bg-gray-100">
                    <button
                      onClick={() => handleQuickCreateOrder("single-dropoff")}
                      className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                    >
                      <Icon name="locationPin" className="h-4 w-4" />
                      <span className="font-normal text-xs">
                        Single Dropoff
                      </span>
                    </button>
                  </MenuItem>
                  <MenuItem as="div" className="hover:bg-gray-100">
                    <button
                      onClick={() => handleQuickCreateOrder("single-p2p")}
                      className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                    >
                      <Icon name="locationPin" className="h-4 w-4" />
                      <span className="font-normal text-xs">Single P2P</span>
                    </button>
                  </MenuItem>
                  <div className="border-t border-gray-200 my-1" />
                  <MenuItem as="div" className="hover:bg-gray-100">
                    <button
                      onClick={() => handleQuickCreateRoute("first-mile")}
                      className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                    >
                      <Icon name="route" className="h-4 w-4" />
                      <span className="font-normal text-xs">
                        First Mile Route
                      </span>
                    </button>
                  </MenuItem>
                  <MenuItem as="div" className="hover:bg-gray-100">
                    <button
                      onClick={() => handleQuickCreateRoute("last-mile")}
                      className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                    >
                      <Icon name="route" className="h-4 w-4" />
                      <span className="font-normal text-xs">
                        Last Mile Route
                      </span>
                    </button>
                  </MenuItem>
                </>
              )}
            </MenuItems>
          </Menu>
        )}
        {routeModalMode !== "edit" && !isDateRange && (
          <Menu as="div">
            <MenuButton>
              <Button
                icon="route"
                iconClassName="hidden 2xl:flex"
                size="sm"
                color="light"
              >
                Routed Orders
              </Button>
            </MenuButton>

            <MenuItems
              transition
              anchor="bottom start"
              className="z-50 w-[200px] origin-top-right mt-1 shadow-md rounded-xl border border-white bg-white p-1 font-normal text-black transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            >
              <MenuItem as="div" className="hover:bg-gray-100">
                <button
                  onClick={() => {
                    useDispatchStore.setState({
                      focusedOrderId: null,
                      focusedRouteId: "new",
                      routeModalMode: "edit",
                    });
                  }}
                  className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                >
                  <Icon name="route" className="h-4 w-4" />
                  <span className="font-normal text-xs">Create Route</span>
                </button>
              </MenuItem>
              <MenuItem as="div" className="hover:bg-gray-100">
                <button
                  onClick={() => setIsBulkOptimizeRoutesModalOpen(true)}
                  className="group flex w-full items-center gap-2 rounded-lg py-2 px-3 data-[focus]:bg-black data-[focus]:text-white"
                >
                  <Icon name="route" className="h-4 w-4" />
                  <span className="font-normal text-xs">
                    Bulk Optimize Routes
                  </span>
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        )}
        {selectedSidebarOrderIds.length >= 1 && (
          <BulkActionsTooltip
            className="!h-[33.8px]"
            selectedOrderIds={selectedSidebarOrderIds}
            selectedRouteIds={selectedSidebarRouteIds}
            onSuccess={() => null}
            onUnselectAllOrders={() => null}
          />
        )}
      </div>
      <div className="flex gap-3 items-center">
        {debouncedSearchOrders && (
          <Input
            type="search"
            placeholder="Search orders by ID, address, phone and external ID"
            onChange={(searchText) => debouncedSearchOrders(searchText)}
            className="!py-[5px]"
          />
        )}
        {!isDateRange && (
          <MarketsDropdown appFilter={appFilter} setAppFilter={setAppFilter} />
        )}
        <OrdersFilterDropdown
          appFilter={appFilter}
          setAppFilter={setAppFilter}
          isDateRange={isDateRange}
        />
        {columnDefs && setColumnDefs && (
          <ColumnDropdown
            columnDefs={columnDefs}
            setColumnDefs={setColumnDefs}
          />
        )}
        {onExport && (
          <Button onClick={onExport} icon="fasFileExport" plain>
            Export
          </Button>
        )}
      </div>

      <CreateOrderDrawer
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
      />
      {isBulkImportModalOpen && user && (
        <Suspense fallback={null}>
          <BulkImportModal
            isOpen
            setIsOpen={setIsBulkImportModalOpen}
            currentWorkspaceName={user.currentWorkspace.name}
            workspaceId={user.currentWorkspace.id}
          />
        </Suspense>
      )}
      {isBulkOptimizeRoutesModalOpen && user && (
        <Suspense fallback={null}>
          <BulkOptimizeRoutesModal
            isOpen
            setIsOpen={(open) => {
              setIsBulkOptimizeRoutesModalOpen(open);
              if (!open) {
                useDispatchStore.setState({ pendingBroJobId: null });
              }
            }}
            workspaceId={user.currentWorkspace.id}
            initialJobId={pendingBroJobId ?? undefined}
          />
        </Suspense>
      )}
    </div>
  );
}
